'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
    name: string;
}

interface WhatIsSoftwareSectionProps {
    plan: Plan;
    translationNamespace: string;
    imageSrc: string;
    descriptionKeys?: string[];
}

export function WhatIsSoftwareSection({ plan, translationNamespace, imageSrc, descriptionKeys }: WhatIsSoftwareSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    // Default to description1, description2 if not provided
    const keys = descriptionKeys || ['description1', 'description2'];

    // Fallback: if description1 doesn't exist, try 'description'
    const getDescription = (key: string) => {
        const value = t(key);
        return value && value !== key ? value : null;
    };

    // Try to get descriptions in order
    const descriptions = keys.map(key => getDescription(key)).filter(Boolean);

    return (
        <SectionWrapper ref={sectionRef.ref} variant="default" padding="md" className="relative bg-white dark:bg-gray-950">
            <div className="container">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className={cn(
                        "transition-all duration-700",
                        sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                    )}>
                        <Typography variant="h2" className="mb-6 text-gray-900 dark:text-white">
                            {t('title')}
                        </Typography>
                        <div className="space-y-6 text-gray-600 dark:text-gray-400 leading-relaxed">
                            {descriptions.length > 0 ? (
                                descriptions.map((desc, idx) => (
                                    <Typography
                                        key={idx}
                                        variant="p"
                                    >
                                        {desc}
                                    </Typography>
                                ))
                            ) : (
                                // Fallback: try 'description' if no description1/description2
                                <Typography variant="p">
                                    {t('description')}
                                </Typography>
                            )}
                        </div>
                    </div>
                    <div className={cn(
                        "relative transition-all duration-700",
                        sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                    )} style={{ transitionDelay: '200ms' }}>
                        <div className="mt-4 md:mt-0 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 shadow-lg">
                            <Image
                                src={imageSrc || "/images/mlm-plans/mlm-software-demo.webp"}
                                alt={t('imageAlt')}
                                width={600}
                                height={400}
                                className="w-full h-full object-contain"
                                unoptimized
                            />
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

